import { jwtDecode } from 'jwt-decode';

// The manager-refresh cookie (hubert-manager / embedded-hubert-manager) is shared across every
// tab on this domain, but sessionStorage is per-tab (and per-origin, so an embedded iframe's
// sessionStorage is isolated from its parent page's too). Tagging a tab with the user id it
// expects to be lets it detect when a *different* tab overwrote that shared cookie - e.g. two
// recruiter-app tabs bridging different users' sessions into the same embedded-manager cookie.
const EXPECTED_USER_ID_KEY = 'hubert_expected_user_id';

const decodeUserId = (accessToken: string): string | null => {
  try {
    const { userId } = jwtDecode<{ userId?: string }>(accessToken);
    return userId ?? null;
  } catch {
    return null;
  }
};

export const setExpectedUserId = (accessToken: string) => {
  const userId = decodeUserId(accessToken);
  if (userId) {
    sessionStorage.setItem(EXPECTED_USER_ID_KEY, userId);
  }
};

export const getExpectedUserId = () => sessionStorage.getItem(EXPECTED_USER_ID_KEY);

export const clearExpectedUserId = () => {
  sessionStorage.removeItem(EXPECTED_USER_ID_KEY);
};

export const matchesExpectedUserId = (accessToken: string, trustedHint?: string | null): boolean => {
  const expected = trustedHint ?? getExpectedUserId();
  if (!expected) {
    return true;
  }
  return decodeUserId(accessToken) === expected;
};

// Set right before ending a session due to a detected mismatch. A subsequent bootstrap (e.g.
// MainLayout's mount effect, which runs regardless of which route a hard redirect lands on)
// would otherwise happily re-authenticate off whatever the shared cookie holds by then - which
// is still a *valid* token, just for the wrong person. This flag tells that one bootstrap to
// skip the cookie entirely rather than silently marking the app logged-in as that user again.
const SKIP_AUTO_LOGIN_KEY = 'hubert_skip_auto_login';

export const setSkipAutoLogin = () => {
  sessionStorage.setItem(SKIP_AUTO_LOGIN_KEY, '1');
};

export const consumeSkipAutoLogin = (): boolean => {
  const shouldSkip = sessionStorage.getItem(SKIP_AUTO_LOGIN_KEY) === '1';
  sessionStorage.removeItem(SKIP_AUTO_LOGIN_KEY);
  return shouldSkip;
};
